import type { Prisma } from '@duke-hq/svc-api-db/client';
import type {
  ProviderInvoiceRuleView,
  ProviderInvoiceRuleFormValues,
  ProviderInvoiceRuleCreatePayload,
} from '@duke-hq/billing/types/rules';

/** Prisma entity → View */
export function toRuleView(
  entity: Prisma.ProviderInvoiceRuleGetPayload<object>,
): ProviderInvoiceRuleView {
  return {
    id: entity.id,
    providerId: entity.providerId,
    type: entity.type,
    courseId: entity.courseId,
    enrolmentContractType: entity.enrolmentContractType,
    enrolmentContractCreatedFrom: entity.enrolmentContractCreatedFrom ?? null,
    enrolmentContractCreatedTo: entity.enrolmentContractCreatedTo ?? null,
    active: entity.active,
    validFrom: entity.validFrom ?? null,
    validTo: entity.validTo ?? null,
    priority: entity.priority,
    dayOfMonth: entity.dayOfMonth ?? null,
    netDays: entity.netDays ?? null,
    priceOverride: entity.priceOverride?.toString() ?? null,
    discountPercent: entity.discountPercent?.toString() ?? null,
    discountFixed: entity.discountFixed?.toString() ?? null,
    firstN: entity.firstN ?? null,
    counterPeriod: entity.counterPeriod ?? null,
  };
}

/** Form → API create payload */
export function formToCreatePayload(
  form: ProviderInvoiceRuleFormValues,
): ProviderInvoiceRuleCreatePayload {
  const parseDate = (s?: string | null) =>
    s == null || s.trim() === '' ? null : new Date(`${s}T00:00:00.000Z`);

  return {
    providerId: form.providerId,
    type: form.type,

    courseId: form.courseId ?? null,
    enrolmentContractType: form.enrolmentContractType ?? null,
    enrolmentContractCreatedFrom: parseDate(form.enrolmentContractCreatedFrom),
    enrolmentContractCreatedTo: parseDate(form.enrolmentContractCreatedTo),

    active: !!form.active,
    validFrom: parseDate(form.validFrom),
    validTo: parseDate(form.validTo),
    priority: form.priority ?? 100,

    dayOfMonth: form.dayOfMonth ?? null,
    netDays: form.netDays ?? null,

    priceOverride: nullIfEmpty(form.priceOverride),
    discountPercent: nullIfEmpty(form.discountPercent),
    discountFixed: nullIfEmpty(form.discountFixed),

    firstN: form.firstN ?? null,
    counterPeriod: form.counterPeriod ?? null,
  };
}

/** View → Form (for edit mode) */
export function viewToFormInitial(view: ProviderInvoiceRuleView): ProviderInvoiceRuleFormValues {
  const fmt = (d?: Date | null) => (d ? d.toISOString().slice(0, 10) : null);
  return {
    providerId: view.providerId,
    type: view.type,

    courseId: view.courseId ?? null,
    enrolmentContractType: view.enrolmentContractType ?? null,
    enrolmentContractCreatedFrom: fmt(view.enrolmentContractCreatedFrom),
    enrolmentContractCreatedTo: fmt(view.enrolmentContractCreatedTo),

    active: view.active,
    validFrom: fmt(view.validFrom),
    validTo: fmt(view.validTo),
    priority: view.priority,

    dayOfMonth: view.dayOfMonth ?? null,
    netDays: view.netDays ?? null,

    priceOverride: view.priceOverride ?? null,
    discountPercent: view.discountPercent ?? null,
    discountFixed: view.discountFixed ?? null,

    firstN: view.firstN ?? null,
    counterPeriod: view.counterPeriod ?? null,
  };
}

function nullIfEmpty<T extends string | null | undefined>(v: T): T | null {
  if (v == null) {
    return null;
  }
  return v.trim() === '' ? null : v;
}
